import { useState, useEffect } from 'react';
import Nav from '../components/common/Nav';

export default function Editor() {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const formatText = (command) => {
    document.execCommand(command, false, null);
    document.getElementById('content-editor')?.focus();
  };

  const insertHeading = (tag) => {
    const selection = window.getSelection();
    const text = selection?.toString() || 'Heading text';
    document.execCommand('insertHTML', false, `<${tag}>${text}</${tag}><p></p>`);
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      const text = window.getSelection()?.toString() || 'Link text';
      document.execCommand('insertHTML', false, `<a href="${url}" target="_blank">${text}</a>`);
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image path (e.g., /assets/images/my-image.jpg):');
    if (url) {
      const alt = prompt('Enter image description:') || 'Image';
      document.execCommand('insertHTML', false, 
        `<figure style="margin: 20px 0; text-align: center;">
          <img src="${url}" alt="${alt}" style="max-width: 100%; height: auto;" loading="lazy">
        </figure><p></p>`
      );
    }
  };

  const saveBlogPost = async () => {
    const contentEditor = document.getElementById('content-editor');
    const contentHtml = contentEditor?.innerHTML || '';

    if (!title || !date || !excerpt || !contentHtml.trim()) {
      setErrorMsg('Please fill in all required fields');
      setTimeout(() => setErrorMsg(''), 5000);
      return;
    }

    try {
      const response = await fetch('/api/blog/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          date,
          excerpt,
          content: contentHtml
        })
      });

      if (!response.ok) throw new Error('Failed to save blog post');

      const result = await response.json();
      setSuccessMsg(`✅ Blog post saved! View at: <a href="${result.url}" target="_blank">${result.url}</a>`);
      
      setTimeout(() => {
        if (confirm('Blog post created! Create another one?')) {
          window.location.reload();
        }
      }, 2000);

    } catch (error) {
      setErrorMsg('Error saving blog post: ' + error.message);
      setTimeout(() => setErrorMsg(''), 5000);
    }
  };

  return (
    <div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
      <Nav />
      
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px', paddingTop: '96px' }}>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h1 style={{ fontSize: '24px', marginBottom: '10px' }}>📝 Blog Post Editor</h1>
          <p>Write your blog post below. The preview updates automatically.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Editor Panel */}
          <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
            <div style={{ padding: '15px 20px', borderBottom: '1px solid #e5e5e5', fontWeight: 600 }}>
              Editor
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              {successMsg && (
                <div style={{ background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724', padding: '12px', borderRadius: '4px', marginBottom: '15px' }} 
                     dangerouslySetInnerHTML={{ __html: successMsg }} />
              )}
              {errorMsg && (
                <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24', padding: '12px', borderRadius: '4px', marginBottom: '15px' }}>
                  {errorMsg}
                </div>
              )}

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '14px' }}>Post Title *</label>
                <input 
                  type="text" 
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="My Awesome Blog Post" 
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '14px' }}>Publication Date *</label>
                <input 
                  type="date" 
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '14px' }}>Short Excerpt *</label>
                <textarea 
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="A brief description (shows in blog listings and SEO)"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', minHeight: '80px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '14px' }}>Content *</label>
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', padding: '10px', background: '#f9f9f9', borderBottom: '1px solid #e5e5e5' }}>
                  <button onClick={() => formatText('bold')} style={{ padding: '8px 12px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' }}><b>B</b></button>
                  <button onClick={() => formatText('italic')} style={{ padding: '8px 12px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' }}><i>I</i></button>
                  <button onClick={() => insertHeading('h2')} style={{ padding: '8px 12px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' }}>H2</button>
                  <button onClick={() => insertHeading('h3')} style={{ padding: '8px 12px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' }}>H3</button>
                  <button onClick={insertLink} style={{ padding: '8px 12px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' }}>🔗 Link</button>
                  <button onClick={insertImage} style={{ padding: '8px 12px', border: '1px solid #ddd', background: 'white', borderRadius: '4px', cursor: 'pointer' }}>🖼️ Image</button>
                </div>
                <div 
                  id="content-editor"
                  contentEditable="true" 
                  style={{ minHeight: '300px', border: '1px solid #ddd', padding: '15px', borderRadius: '4px', lineHeight: 1.6, fontSize: '16px', outline: 'none' }}
                  onInput={(e) => setContent(e.currentTarget.innerHTML)}
                />
              </div>

              <button 
                onClick={saveBlogPost}
                style={{ background: '#000', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, width: '100%' }}
              >
                💾 Save Blog Post
              </button>
            </div>
          </div>

          {/* Preview Panel */}
          <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
            <div style={{ padding: '15px 20px', borderBottom: '1px solid #e5e5e5', fontWeight: 600 }}>
              Live Preview
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px', fontFamily: '"SF Mono", "Monaco", "Inconsolata", monospace', fontSize: '14px', lineHeight: 1.7 }}>
              <div className="post-date">[{date}]</div>
              <h1>{title || 'Your Blog Post Title'}</h1>
              <hr style={{ border: 'none', borderTop: '1px solid #e5e5e5', margin: '20px 0' }} />
              <div dangerouslySetInnerHTML={{ __html: content || '<p>Your content will appear here as you type...</p>' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

